import QRCodeStyling from "qr-code-styling";
import type { Ecc } from "./capacity";
import { logoForColor } from "./logo";
import { buildOptions, type QrStyle } from "./style";

export type ExportFormat = "png" | "svg";

/** Create a styled QR code instance (not yet attached to the page). Resolves the tinted logo if needed. */
export async function createStyledCode(text: string, ecc: Ecc, sizePx: number, style: QrStyle): Promise<QRCodeStyling> {
	const logo = style.logo ? await logoForColor(style.color) : "";
	return new QRCodeStyling(buildOptions(text, ecc, sizePx, style, logo));
}

/** Render into `container`, replacing whatever was there. Drawing completes asynchronously. */
export async function renderInto(container: HTMLElement, text: string, ecc: Ecc, sizePx: number, style: QrStyle): Promise<QRCodeStyling> {
	const code = await createStyledCode(text, ecc, sizePx, style);
	container.replaceChildren();
	code.append(container);
	return code;
}

/**
 * Render a PNG preview as a detached, decoded <img>. An image (rather than a canvas) lets phones
 * long-press to "Save to Photos" and desktops right-click to copy. The src is an object URL that
 * `attachPreview` revokes when it is replaced; revoke it yourself if you discard the image.
 */
export async function previewImage(text: string, ecc: Ecc, sizePx: number, style: QrStyle, alt = "QR code"): Promise<HTMLImageElement> {
	const blob = await exportBlob(text, ecc, sizePx, style, "png");
	const img = document.createElement("img");
	img.alt = alt;
	img.decoding = "async";
	img.src = URL.createObjectURL(blob);
	await img.decode().catch(() => undefined);
	return img;
}

/** Put a preview image into the container, releasing the previous image's object URL. */
export function attachPreview(container: HTMLElement, img: HTMLImageElement): void {
	const previous = container.dataset.previewUrl;
	container.replaceChildren(img);
	container.dataset.previewUrl = img.src;
	if (previous && previous !== img.src) URL.revokeObjectURL(previous);
}

/** Render an export at the requested size and return it as a Blob. */
export async function exportBlob(text: string, ecc: Ecc, sizePx: number, style: QrStyle, format: ExportFormat): Promise<Blob> {
	const code = await createStyledCode(text, ecc, sizePx, style);
	const data = await code.getRawData(format);
	if (!(data instanceof Blob)) throw new Error("Export failed");
	return data;
}
